(function initUnboundSearchApi() {
  const DEFAULT_PREDICTIVE_LIMIT = 8;
  const DEFAULT_RESULTS_PAGE_SIZE = 24;
  let cachedHelperClient = undefined;

  function getConfig() {
    return (
      window.UNBOUND_SEARCH_CLIENT_CONFIG ||
      window.UNBOUND_SEARCH_API_CONFIG ||
      window.UNBOUND_SEARCH_TRACKING_CONFIG ||
      {}
    );
  }

  function normalizeValue(value) {
    return String(value || "").trim().replace(/\s+/g, " ");
  }

  function sanitizeValue(value) {
    return normalizeValue(value);
  }

  function normalizeMarketId(value) {
    const normalized = sanitizeValue(value).toLowerCase();

    if (!normalized) return "";
    if (normalized === "us" || normalized === "ca" || normalized === "intl") return normalized;

    return "intl";
  }

  function getPageContext() {
    const config = getConfig();
    const pageContext = config.pageContext || config.helperContext || config || {};

    return {
      marketId: normalizeMarketId(config.marketId),
      contextType: sanitizeValue(pageContext.contextType || pageContext.type).toLowerCase(),
      contextHandle: sanitizeValue(pageContext.contextHandle || pageContext.handle),
      contextGender: sanitizeValue(
        pageContext.contextGender || pageContext.gender,
      ).toLowerCase(),
    };
  }

  function isConfigured() {
    const config = getConfig();
    return Boolean(
      sanitizeValue(config.apiBaseUrl || config.appBaseUrl) && sanitizeValue(config.shop),
    );
  }

  function buildAppUrl(pathname, params = {}) {
    const config = getConfig();
    const baseUrl = sanitizeValue(config.apiBaseUrl || config.appBaseUrl).replace(/\/+$/, "");
    if (!baseUrl) return "";

    const url = new URL(`${baseUrl}${pathname}`);

    Object.entries(params).forEach(([key, value]) => {
      const normalizedValue = sanitizeValue(value);
      if (normalizedValue) {
        url.searchParams.set(key, normalizedValue);
      }
    });

    return url.toString();
  }

  function getRedirectUrl(redirect) {
    if (!redirect) return "";
    if (typeof redirect === "string") return redirect;

    return sanitizeValue(
      redirect.url ||
        redirect.targetUrl ||
        redirect.path ||
        redirect.href ||
        redirect.destinationUrl,
    );
  }

  function normalizeProductId(rawValue) {
    const normalized = sanitizeValue(rawValue);
    if (!normalized) return "";
    if (normalized.startsWith("gid://")) return normalized;

    const numericMatch = normalized.match(/\d+/)?.[0];
    return numericMatch ? `gid://shopify/Product/${numericMatch}` : normalized;
  }

  function normalizeProduct(product = {}) {
    return {
      id: normalizeProductId(product.id || product.admin_graphql_api_id),
      productId: normalizeProductId(product.id || product.admin_graphql_api_id),
      handle: sanitizeValue(product.handle),
      title: sanitizeValue(product.title),
      productType: sanitizeValue(product.productType || product.product_type),
      vendor: sanitizeValue(product.vendor),
      available: Boolean(product.available),
      url: sanitizeValue(product.url),
      image: product.image || product.featured_image || null,
      price: product.price || null,
      collectionIds: Array.isArray(product.collectionIds) ? product.collectionIds : [],
      collectionHandles: Array.isArray(product.collectionHandles) ? product.collectionHandles : [],
      marketIds: Array.isArray(product.marketIds) ? product.marketIds : [],
      availableMarketIds: Array.isArray(product.availableMarketIds)
        ? product.availableMarketIds
        : [],
      genderContexts: Array.isArray(product.genderContexts) ? product.genderContexts : [],
    };
  }

  function normalizePredictiveSuggestionItem(item = {}, index = 0) {
    const itemType = sanitizeValue(item.itemType || item.type).toLowerCase();
    const isCollectionItem = itemType === "collection";
    const label = sanitizeValue(item.label || item.query || item.targetQuery || item.title);
    const targetQuery = sanitizeValue(item.targetQuery || item.query || item.label || item.title);

    return {
      label,
      query: isCollectionItem ? label : targetQuery,
      targetQuery: isCollectionItem ? label : targetQuery,
      targetUrl: sanitizeValue(item.targetUrl || item.url),
      source: sanitizeValue(item.source),
      position: Number.parseInt(item.position, 10) || index + 1,
      itemType: isCollectionItem ? "collection" : "query",
    };
  }

  function normalizePredictiveCollectionItem(item = {}, index = 0, offset = 0) {
    return {
      label: sanitizeValue(item.label || item.title || item.query),
      query: sanitizeValue(item.title || item.label || item.query),
      targetQuery: sanitizeValue(item.title || item.label || item.query),
      targetUrl: sanitizeValue(item.targetUrl || item.url),
      source: sanitizeValue(item.source || "COLLECTION"),
      position: Number.parseInt(item.position, 10) || offset + index + 1,
      itemType: "collection",
    };
  }

  function buildPredictiveParams(query, options = {}) {
    const config = getConfig();
    const pageContext = getPageContext();
    const context = options.context || {};

    return {
      q: normalizeValue(query),
      shop: sanitizeValue(config.shop),
      limit: String(options.limit || DEFAULT_PREDICTIVE_LIMIT),
      marketId: normalizeMarketId(context.marketId || pageContext.marketId),
      contextGender: sanitizeValue(context.contextGender || pageContext.contextGender),
      contextType: sanitizeValue(context.contextType || pageContext.contextType),
      contextHandle: sanitizeValue(context.contextHandle || pageContext.contextHandle),
    };
  }

  function buildResultsParams(query, options = {}) {
    const config = getConfig();
    const pageContext = getPageContext();
    const context = options.context || {};

    return {
      q: normalizeValue(query),
      shop: sanitizeValue(config.shop),
      page: String(options.page || 1),
      pageSize: String(options.pageSize || DEFAULT_RESULTS_PAGE_SIZE),
      marketId: normalizeMarketId(context.marketId || pageContext.marketId),
      contextGender: sanitizeValue(context.contextGender || pageContext.contextGender),
      contextType: sanitizeValue(context.contextType || pageContext.contextType),
      contextHandle: sanitizeValue(context.contextHandle || pageContext.contextHandle),
    };
  }

  function buildHelperClientConfig() {
    const config = getConfig();
    const pageContext = getPageContext();

    if (!isConfigured()) return null;

    return {
      apiBaseUrl: sanitizeValue(config.apiBaseUrl || config.appBaseUrl),
      shop: sanitizeValue(config.shop),
      marketId: normalizeMarketId(config.marketId || pageContext.marketId),
      contextGender: sanitizeValue(pageContext.contextGender),
      contextType: sanitizeValue(pageContext.contextType),
      contextHandle: sanitizeValue(pageContext.contextHandle),
    };
  }

  function getHelperClient() {
    if (cachedHelperClient !== undefined) {
      return cachedHelperClient;
    }

    const helperFactory = window.UnboundSearchThemeClient?.createSearchClient;
    const helperConfig = buildHelperClientConfig();

    if (typeof helperFactory !== "function" || !helperConfig) {
      cachedHelperClient = null;
      return cachedHelperClient;
    }

    try {
      cachedHelperClient = helperFactory(helperConfig);
    } catch {
      cachedHelperClient = null;
    }

    return cachedHelperClient;
  }

  function buildHelperOverrides(options = {}) {
    const pageContext = getPageContext();
    const context = options.context || {};

    return {
      limit: options.limit || undefined,
      page: options.page || undefined,
      pageSize: options.pageSize || undefined,
      marketId: normalizeMarketId(context.marketId || pageContext.marketId),
      contextGender: sanitizeValue(context.contextGender || pageContext.contextGender),
      contextType: sanitizeValue(context.contextType || pageContext.contextType),
      contextHandle: sanitizeValue(context.contextHandle || pageContext.contextHandle),
    };
  }

  async function fetchJson(url, { signal } = {}) {
    const response = await fetch(url, { signal });
    if (!response.ok) {
      throw new Error(`Search API request failed with status ${response.status}`);
    }

    return response.json();
  }

  function emptyPredictiveResponse(query = "") {
    return {
      source: "empty",
      query: normalizeValue(query),
      normalizedQuery: normalizeValue(query),
      suggestions: [],
      products: [],
      redirect: null,
      banner: null,
      meta: {},
      resultCount: 0,
      zeroResults: true,
    };
  }

  function emptyResultsResponse(query = "", page = 1, pageSize = DEFAULT_RESULTS_PAGE_SIZE) {
    return {
      source: "empty",
      query: normalizeValue(query),
      normalizedQuery: normalizeValue(query),
      products: [],
      redirect: null,
      banner: null,
      pagination: {
        page,
        pageSize,
        total: 0,
        totalPages: 0,
      },
      meta: {},
      resultCount: 0,
      zeroResults: true,
    };
  }

  function normalizeAppPredictiveResponse(payload = {}, query = "") {
    const normalizedSuggestions = Array.isArray(payload.suggestions)
      ? payload.suggestions
          .map((item, index) => normalizePredictiveSuggestionItem(item, index))
          .filter((item) => item.label)
      : [];
    const normalizedCollections = Array.isArray(payload.collections)
      ? payload.collections
          .map((item, index) =>
            normalizePredictiveCollectionItem(item, index, normalizedSuggestions.length),
          )
          .filter((item) => item.label)
      : [];
    const suggestions = [...normalizedSuggestions, ...normalizedCollections];

    const products = Array.isArray(payload.products)
      ? payload.products.map((product) => normalizeProduct(product)).filter((product) => product.title)
      : [];

    return {
      source: "app",
      query: sanitizeValue(payload.query || query),
      normalizedQuery: sanitizeValue(payload.normalizedQuery || payload.query || query),
      suggestions,
      products,
      redirect: payload.redirect || null,
      banner: payload.banner || null,
      meta: payload.meta || {},
      resultCount: suggestions.length + products.length,
      zeroResults: Boolean(payload.meta?.zeroResults),
    };
  }

  function normalizeShopifyPredictiveResponse(payload = {}, query = "") {
    const resources = payload?.resources?.results || {};
    const suggestions = [
      ...(Array.isArray(resources.queries)
        ? resources.queries.map((item, index) => ({
            label: sanitizeValue(item.text || item.styled_text || item.title),
            query: sanitizeValue(item.text || item.styled_text || item.title),
            targetQuery: sanitizeValue(item.text || item.styled_text || item.title),
            targetUrl: "",
            source: "SHOPIFY_QUERY",
            position: index + 1,
            itemType: "query",
          }))
        : []),
      ...(Array.isArray(resources.collections)
        ? resources.collections.map((item, index) => ({
            label: sanitizeValue(item.title),
            query: sanitizeValue(item.title),
            targetQuery: sanitizeValue(item.title),
            targetUrl: sanitizeValue(item.url),
            source: "SHOPIFY_COLLECTION",
            position:
              (Array.isArray(resources.queries) ? resources.queries.length : 0) + index + 1,
            itemType: "collection",
          }))
        : []),
    ].filter((item) => item.label);

    const products = Array.isArray(resources.products)
      ? resources.products
          .map((product) =>
            normalizeProduct({
              ...product,
              id: product.admin_graphql_api_id || product.id,
              productType: product.product_type,
              image: product.featured_image || null,
            }),
          )
          .filter((product) => product.title)
      : [];

    const resultCount = suggestions.length + products.length;

    return {
      source: "shopify",
      query: normalizeValue(query),
      normalizedQuery: normalizeValue(query),
      suggestions,
      products,
      redirect: null,
      banner: null,
      meta: {},
      resultCount,
      zeroResults: resultCount === 0,
    };
  }

  function normalizeShopifyCollectionSuggestions(payload = {}) {
    const collections = payload?.resources?.results?.collections;

    if (!Array.isArray(collections)) {
      return [];
    }

    return collections
      .map((item, index) => ({
        label: sanitizeValue(item.title),
        query: sanitizeValue(item.title),
        targetQuery: sanitizeValue(item.title),
        targetUrl: sanitizeValue(item.url),
        source: "SHOPIFY_COLLECTION",
        position: index + 1,
        itemType: "collection",
      }))
      .filter((item) => item.label);
  }

  function mergePredictiveSuggestions(primarySuggestions = [], secondarySuggestions = []) {
    const mergedSuggestions = [];
    const seen = new Set();

    [...primarySuggestions, ...secondarySuggestions].forEach((item) => {
      if (!item?.label) return;

      const key = [
        sanitizeValue(item.itemType || "query").toLowerCase(),
        sanitizeValue(item.label).toLowerCase(),
        sanitizeValue(item.targetUrl).toLowerCase(),
      ].join("::");

      if (seen.has(key)) return;

      seen.add(key);
      mergedSuggestions.push({
        ...item,
        position: mergedSuggestions.length + 1,
      });
    });

    return mergedSuggestions;
  }

  function mergePredictiveResponseCollections(response, collections = []) {
    const mergedSuggestions = mergePredictiveSuggestions(response?.suggestions || [], collections);
    const products = Array.isArray(response?.products) ? response.products : [];
    const resultCount = mergedSuggestions.length + products.length;

    return {
      ...(response || emptyPredictiveResponse()),
      suggestions: mergedSuggestions,
      resultCount,
      zeroResults: resultCount === 0,
    };
  }

  function normalizeAppResultsResponse(payload = {}, query = "", page = 1, pageSize = DEFAULT_RESULTS_PAGE_SIZE) {
    const products = Array.isArray(payload.products)
      ? payload.products.map((product) => normalizeProduct(product)).filter((product) => product.title)
      : [];

    const pagination = payload.pagination || {};
    const total = Number.parseInt(pagination.total, 10);
    const totalPages = Number.parseInt(pagination.totalPages, 10);

    return {
      source: "app",
      query: sanitizeValue(payload.query || query),
      normalizedQuery: sanitizeValue(payload.normalizedQuery || payload.query || query),
      products,
      redirect: payload.redirect || null,
      banner: payload.banner || null,
      pagination: {
        page: Number.parseInt(pagination.page, 10) || page,
        pageSize: Number.parseInt(pagination.pageSize, 10) || pageSize,
        total: Number.isFinite(total) ? total : products.length,
        totalPages: Number.isFinite(totalPages) ? totalPages : 0,
      },
      meta: payload.meta || {},
      resultCount: Number.isFinite(total) ? total : products.length,
      zeroResults: Boolean(payload.meta?.zeroResults),
    };
  }

  async function fetchShopifyPredictive(query, { signal } = {}) {
    const normalizedQuery = normalizeValue(query);
    if (!normalizedQuery) {
      return emptyPredictiveResponse(normalizedQuery);
    }

    const predictiveSearchUrl = `${window.routes?.predictive_search_url || "/search/suggest"}.json`;
    const url = new URL(predictiveSearchUrl, window.location.origin);
    url.searchParams.set("q", normalizedQuery);
    url.searchParams.set("resources[type]", "product,query,collection");
    url.searchParams.set("resources[limit]", "4");
    url.searchParams.set("resources[limit_scope]", "each");
    url.searchParams.set("resources[options][unavailable_products]", "hide");
    url.searchParams.set("resources[options][fields]", "title,product_type,variants.title,vendor");

    const payload = await fetchJson(url.toString(), { signal });
    return normalizeShopifyPredictiveResponse(payload, normalizedQuery);
  }

  async function fetchShopifyCollections(query, { signal, limit } = {}) {
    const normalizedQuery = normalizeValue(query);
    if (!normalizedQuery) {
      return [];
    }

    const predictiveSearchUrl = `${window.routes?.predictive_search_url || "/search/suggest"}.json`;
    const url = new URL(predictiveSearchUrl, window.location.origin);
    url.searchParams.set("q", normalizedQuery);
    url.searchParams.set("resources[type]", "collection");
    url.searchParams.set("resources[limit]", String(limit || 4));

    const payload = await fetchJson(url.toString(), { signal });
    return normalizeShopifyCollectionSuggestions(payload);
  }

  async function fetchPredictive(query, options = {}) {
    const normalizedQuery = normalizeValue(query);
    if (!normalizedQuery) {
      return emptyPredictiveResponse(normalizedQuery);
    }

    const helperClient = getHelperClient();
    if (helperClient?.predictive) {
      try {
        const [payload, shopifyCollections] = await Promise.all([
          helperClient.predictive(
            normalizedQuery,
            buildHelperOverrides(options),
          ),
          fetchShopifyCollections(normalizedQuery, {
            signal: options.signal,
            limit: options.limit,
          }).catch(() => []),
        ]);

        return mergePredictiveResponseCollections(
          normalizeAppPredictiveResponse(payload, normalizedQuery),
          shopifyCollections,
        );
      } catch (error) {
        if (error.name === "AbortError") throw error;
      }
    }

    if (isConfigured()) {
      try {
        const url = buildAppUrl("/api/search/predictive", buildPredictiveParams(normalizedQuery, options));
        const [payload, shopifyCollections] = await Promise.all([
          fetchJson(url, { signal: options.signal }),
          fetchShopifyCollections(normalizedQuery, {
            signal: options.signal,
            limit: options.limit,
          }).catch(() => []),
        ]);

        return mergePredictiveResponseCollections(
          normalizeAppPredictiveResponse(payload, normalizedQuery),
          shopifyCollections,
        );
      } catch (error) {
        if (error.name === "AbortError") throw error;
      }
    }

    try {
      return await fetchShopifyPredictive(normalizedQuery, { signal: options.signal });
    } catch (error) {
      if (error.name === "AbortError") throw error;
      return emptyPredictiveResponse(normalizedQuery);
    }
  }

  async function fetchResults(query, options = {}) {
    const normalizedQuery = normalizeValue(query);
    const page = Number.parseInt(options.page, 10) || 1;
    const pageSize = Number.parseInt(options.pageSize, 10) || DEFAULT_RESULTS_PAGE_SIZE;

    if (!normalizedQuery || !isConfigured()) {
      return null;
    }

    const helperClient = getHelperClient();
    if (helperClient?.results) {
      const payload = await helperClient.results(normalizedQuery, buildHelperOverrides(options));
      return normalizeAppResultsResponse(payload, normalizedQuery, page, pageSize);
    }

    const url = buildAppUrl("/api/search/results", buildResultsParams(normalizedQuery, options));
    const payload = await fetchJson(url, { signal: options.signal });
    return normalizeAppResultsResponse(payload, normalizedQuery, page, pageSize);
  }

  window.UnboundSearchApi = {
    isConfigured,
    getConfig,
    getClient: getHelperClient,
    getPageContext,
    getRedirectUrl,
    normalizeProductId,
    fetchPredictive,
    fetchResults,
  };
})();
